(() => {
  const VERSION = "enhanced-score-v2";
  const PRIORITY_ORDER = [
    "energy",
    "strength",
    "drive",
    "focus",
    "recovery",
    "complete",
  ];

  const severityWeights = {
    "daytime-symptoms": { yes: 3, no: 0 },
    "afternoon-energy": { drained: 4, lower: 3, slower: 1, same: 0 },
    "change-timeline": { "over-year": 3, "past-year": 2, "past-months": 1, unsure: 1 },
    "body-composition": { lean: 0, average: 1, soft: 2, overweight: 3 },
    sleep: { "under-5": 4, "5-6": 3, "7-8": 0, "over-8": 1 },
    stress: { daily: 4, weekly: 3, occasionally: 1, rarely: 0 },
    nutrition: { rarely: 3, sometimes: 2, most: 1, nearly: 0 },
  };

  const bandCopy = {
    needs_support: {
      badge: "NEEDS SUPPORT",
      level: "HIGH",
      message:
        "Your answers show a meaningful gap between the way you want to perform and the support built into your current routine.",
    },
    building_momentum: {
      badge: "BUILDING MOMENTUM",
      level: "MODERATE",
      message:
        "Your foundation is working, but several daily factors may be making your energy, strength, and recovery less consistent than they could be.",
    },
    strong_foundation: {
      badge: "STRONG FOUNDATION",
      level: "FOCUSED",
      message:
        "Your routine has a solid base. Targeted daily support may help you protect that momentum and keep showing up consistently.",
    },
  };

  function add(scores, key, value) {
    scores[key] = (scores[key] || 0) + value;
  }

  function priorityScores(answers) {
    const scores = Object.fromEntries(PRIORITY_ORDER.map((key) => [key, 0]));
    const concern = answers["primary-concern"];
    if (PRIORITY_ORDER.includes(concern)) add(scores, concern, 4);

    const desired = answers["desired-results"];
    if (desired === "all") add(scores, "complete", 8);
    else if (PRIORITY_ORDER.includes(desired)) add(scores, desired, 5);

    if (answers["daytime-symptoms"] === "yes") {
      add(scores, "energy", 2);
      add(scores, "focus", 2);
    }
    const afternoon = { drained: 3, lower: 2, slower: 1 }[
      answers["afternoon-energy"]
    ] || 0;
    add(scores, "energy", afternoon);

    const body = answers["body-composition"];
    if (body === "soft") add(scores, "strength", 1);
    if (body === "overweight") {
      add(scores, "strength", 2);
      add(scores, "recovery", 1);
    }

    const sleep = answers.sleep;
    if (sleep === "under-5") {
      add(scores, "recovery", 3);
      add(scores, "energy", 1);
    } else if (sleep === "5-6") {
      add(scores, "recovery", 2);
      add(scores, "energy", 1);
    } else if (sleep === "over-8") add(scores, "recovery", 1);

    const stress = answers.stress;
    if (stress === "daily") {
      add(scores, "recovery", 3);
      add(scores, "focus", 1);
    } else if (stress === "weekly") add(scores, "recovery", 2);
    else if (stress === "occasionally") add(scores, "recovery", 1);

    const nutrition = answers.nutrition;
    if (nutrition === "rarely") {
      add(scores, "recovery", 2);
      add(scores, "energy", 1);
    } else if (nutrition === "sometimes") add(scores, "recovery", 1);
    return scores;
  }

  function selectPriority(answers, scores) {
    const desired = answers["desired-results"];
    const concern = answers["primary-concern"];
    return [...PRIORITY_ORDER].sort((left, right) => {
      const difference = scores[right] - scores[left];
      if (difference) return difference;
      if (left === desired) return -1;
      if (right === desired) return 1;
      if (left === concern) return -1;
      if (right === concern) return 1;
      return PRIORITY_ORDER.indexOf(left) - PRIORITY_ORDER.indexOf(right);
    })[0];
  }

  function severityScore(answers) {
    return Object.entries(severityWeights).reduce(
      (total, [question, weights]) =>
        total + (weights[answers[question]] || 0),
      0,
    );
  }

  function resultBand(score) {
    if (score >= 15) return "needs_support";
    if (score >= 7) return "building_momentum";
    return "strong_foundation";
  }

  function limitingPattern(answers) {
    const energy =
      (answers["daytime-symptoms"] === "yes" ? 3 : 0) +
      ({ drained: 4, lower: 3, slower: 1 }[answers["afternoon-energy"]] || 0);
    const recovery =
      ({ "under-5": 4, "5-6": 3, "over-8": 1 }[answers.sleep] || 0) +
      ({ daily: 4, weekly: 3, occasionally: 1 }[answers.stress] || 0);
    const routine =
      ({ soft: 2, overweight: 3, average: 1 }[answers["body-composition"]] || 0) +
      ({ rarely: 3, sometimes: 2, most: 1 }[answers.nutrition] || 0);

    const highest = [
      ["recovery", recovery],
      ["energy", energy],
      ["routine", routine],
    ].sort((left, right) => right[1] - left[1])[0];
    if (!highest[1]) {
      return { pattern: "SOLID FOUNDATION", secondaryFactor: "OPTIMIZATION" };
    }
    if (highest[0] === "recovery") {
      return { pattern: "RECOVERY PRESSURE", secondaryFactor: "SLEEP & STRESS" };
    }
    if (highest[0] === "energy") {
      return { pattern: "ENERGY VARIABILITY", secondaryFactor: "DAILY ENERGY" };
    }
    return {
      pattern: "ROUTINE INCONSISTENCY",
      secondaryFactor: "NUTRITION & BODY COMPOSITION",
    };
  }

  function scoreQuiz(answers) {
    const severity = severityScore(answers);
    const band = resultBand(severity);
    const scores = priorityScores(answers);
    const primaryPriority = selectPriority(answers, scores);
    const pattern = limitingPattern(answers);
    return {
      version: VERSION,
      band,
      severityScore: severity,
      primaryPriority,
      priorityScores: scores,
      pin: Math.round(38 + (Math.min(24, severity) / 24) * 50),
      ...bandCopy[band],
      ...pattern,
    };
  }

  globalThis.LngvtyQuizScoring = Object.freeze({ VERSION, scoreQuiz });
})();
